// PWA Service Worker registration and utilities

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

export function register(config) {
  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/sw.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('This web app is being served cache-first by a service worker.');
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log('New content is available; please refresh.');
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              console.log('Content is cached for offline use.');
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

// PWA Install prompt functionality
let deferredPrompt;
let isInstallable = false;

export function initInstallPrompt() {
  // Check if we're in a supported environment
  if (!window.navigator || !('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return;
  }

  window.addEventListener('beforeinstallprompt', (e) => {
    console.log('PWA install prompt available - beforeinstallprompt fired');
    e.preventDefault();
    deferredPrompt = e;
    isInstallable = true;

    // Dispatch custom event to notify components
    window.dispatchEvent(new CustomEvent('pwa-installable', { detail: true }));
    console.log('PWA installable event dispatched');
  });

  window.addEventListener('appinstalled', () => {
    console.log('PWA was installed - appinstalled fired');
    isInstallable = false;
    deferredPrompt = null;

    // Dispatch custom event to notify components
    window.dispatchEvent(new CustomEvent('pwa-installed'));
  });
}

export function showInstallPrompt() {
  console.log('showInstallPrompt called', { deferredPrompt: !!deferredPrompt, isInstallable });

  if (!deferredPrompt) {
    console.log('No deferred prompt available');

    // Fallback for browsers that don't support beforeinstallprompt
    if (navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) {
      alert('To install this app on iOS: Tap the Share button and then "Add to Home Screen"');
      return;
    }

    if (navigator.userAgent.includes('Android')) {
      alert('To install this app: Open the browser menu and look for "Add to Home Screen" or "Install App"');
      return;
    }

    alert('This app can be installed on your device. Look for an install option in your browser menu.');
    return;
  }

  if (!isInstallable) {
    console.log('App is not installable at this time');
    return;
  }

  console.log('Showing install prompt...');
  deferredPrompt.prompt();

  deferredPrompt.userChoice.then((choiceResult) => {
    console.log('User choice result:', choiceResult);
    if (choiceResult.outcome === 'accepted') {
      console.log('User accepted the PWA install prompt');
    } else {
      console.log('User dismissed the PWA install prompt');
    }
    deferredPrompt = null;
    isInstallable = false;

    // Update components
    window.dispatchEvent(new CustomEvent('pwa-installable', { detail: false }));
  }).catch((error) => {
    console.error('Error showing install prompt:', error);
  });
}

export function getInstallability() {
  return {
    isInstallable,
    canPrompt: !!deferredPrompt
  };
}

// Check if app is running in standalone mode (installed as PWA)
export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://')
  );
}

// Network status utilities
export function initNetworkStatus() {
  const updateOnlineStatus = () => {
    window.dispatchEvent(new CustomEvent('network-status', {
      detail: { online: navigator.onLine }
    }));
  };

  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);

  // Initial status
  updateOnlineStatus();
}

// Background sync for offline actions
export function scheduleBackgroundSync(tag = 'background-sync') {
  if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
    navigator.serviceWorker.ready.then((registration) => {
      return registration.sync.register(tag);
    }).catch((error) => {
      console.log('Background sync registration failed:', error);
    });
  }
}


